import {cdnImage} from '@/util/cdnImage';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {MUIContainer} from '@/components/MUI-Components';
import DisplayImage from '@/components/Ui-components/display-image';
import {borderRadius, colors, fontWeight} from '@/styles/tokens';
const iconTime = cdnImage('white-label-exchange-development-new/icon-time.svg');
const iconCost = cdnImage('white-label-exchange-development-new/icon-cost.svg');
const iconComplexity = cdnImage('white-label-exchange-development-new/icon-complexity.svg');
// ─── Static data ─────────────────────────────────────────────────────────────
const challengesData = [
    {
        icon: iconTime,
        iconAlt: 'Time icon',
        title: '18 months wasted before a single trade',
        body: 'Custom builds take 12–18 months on average. By the time you launch, market conditions shift and competitors capture your users.',
        debutLabel: 'With Debut:',
        debutText: 'Go live in just 6 weeks. No wasted runway.'
    },
    {
        icon: iconCost,
        iconAlt: 'Cost icon',
        title: '$500,000+ before you earn your first fee',
        body: 'Engineering teams, infrastructure, security audits, compliance — building from scratch burns the capital that should be growing your business.',
        debutLabel: 'With Debut:',
        debutText: 'Enterprise-grade platform from $40,000'
    },
    {
        icon: iconComplexity,
        iconAlt: 'Complexity icon',
        title: 'Compliance, liquidity, and security — all at once',
        body: "You need KYC/AML from day one, liquidity from launch, and security that passes institutional scrutiny. Most teams can't handle all three simultaneously.",
        debutLabel: 'With Debut:',
        debutText: 'Pre-integrated, guided from day one'
    }
];

// ─── Module-level sx constants ────────────────────────────────────────────────
const sectionSx = {
    bgcolor: colors.background.subtle
} as const;

const headingSx = {
    fontWeight: fontWeight.extrabold,
    fontSize: {xs: 32, md: 52},
    color: colors.text.primary,
    textAlign: 'center',
    lineHeight: 1.2,
    mb: {xs: 4, md: 6},
    maxWidth: 1000,
    mx: 'auto'
} as const;

const cardSx = {
    bgcolor: colors.background.default,
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: borderRadius['3xl'],
    p: {xs: 3, md: '40px'},
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
} as const;

const iconBoxSx = {
    width: 48,
    height: 48,
    bgcolor: 'rgba(37,99,235,0.1)',
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 3,
    flexShrink: 0
} as const;

const cardTitleSx = {
    fontWeight: fontWeight.semibold,
    fontSize: 20,
    color: '#0f1724',
    lineHeight: '30px',
    mb: 2
} as const;

const cardBodySx = {
    fontSize: 15,
    lineHeight: '25.5px',
    color: colors.text.primary,
    flex: 1
} as const;

const calloutSx = {
    bgcolor: 'rgba(37,99,235,0.05)',
    borderLeft: '3px solid #244486',
    borderRadius: '0 6px 6px 0',
    px: 2,
    py: 1.5,
    mt: 3
} as const;
// ─────────────────────────────────────────────────────────────────────────────

const WhiteLabelChallenges = () => {
    return (
        <Box sx={sectionSx} className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
            <MUIContainer>
                <Typography variant="h2" className="font-headers" sx={headingSx}>
                    Why Most Crypto Exchange Ventures Stall Before Launch
                </Typography>

                <Grid container spacing={3}>
                    {challengesData.map((card, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Box sx={cardSx}>
                                <Box sx={iconBoxSx}>
                                    <DisplayImage src={card.icon} alt={card.iconAlt} width={24} height={24} />
                                </Box>

                                <Typography className="font-headers" sx={cardTitleSx}>
                                    {card.title}
                                </Typography>

                                <Typography className="font-regular-text" sx={cardBodySx}>
                                    {card.body}
                                </Typography>

                                <Box sx={calloutSx}>
                                    <Typography
                                        component="span"
                                        className="font-headers"
                                        sx={{fontSize: 14, fontWeight: fontWeight.semibold, color: colors.primary.deep}}
                                    >
                                        {card.debutLabel}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        className="font-regular-text"
                                        sx={{fontSize: 14, fontWeight: fontWeight.regular, color: '#0f1724'}}
                                    >
                                        {card.debutText}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default WhiteLabelChallenges;
